import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ViewportScroller } from '@angular/common';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ButtonSquareComponent } from '@atoms/button-square/button-square.component';
import { AuthService } from '@services/auth.service';
import { Tool } from './type';
import { TOOLS_LIST } from './constants';
import { BuildingModal } from '../building-modal/building-modal';
import { RegisterModal } from '../register-modal/register-modal';
import { WorkTypeSelectionModalComponent, ConstructionType, WorkTypeModalConfig } from '../../../page/quote/modal/work-type-selection-modal.component';
import { openTrainingUrl } from '@utils/open-training-url';
import { AnalyticsService } from '@services/analytics.service';

@Component({
  selector: 'app-tools-section',
  templateUrl: './tools-section.component.html',
  styleUrls: ['./tools-section.component.scss'],
  standalone: true,
  imports: [CommonModule, BuildingModal, ButtonSquareComponent]
})
export class ToolsSectionComponent {
  showToolConstructionModal = false;
  selectedToolId: string | null = null;
  private auth = inject(AuthService);
  private bottomSheet = inject(MatBottomSheet);
  private analytics = inject(AnalyticsService);

  tools: Tool[] = TOOLS_LIST

  constructor(
    private router: Router,
    private viewportScroller: ViewportScroller
  ) {}

  onToolClick(tool: Tool): void {
    // Actualizar selección: si es el mismo tool, mantener seleccionado, si es otro, cambiar selección
    this.selectedToolId = tool.id;
    
    const currentUser = this.auth.user();

    // Si la herramienta está "próximamente", mostrar modal
    if (tool.isComingSoon) {
      this.openConstructionModal();
      return;
    }

    if((tool.id === 'unicon') && !currentUser) {
      this.openRegisterModal();
    } else if (tool.id === 'training') {
      this.analytics.trackEvent('education', 'tools_section');
      this.openTrainingUrl();
    } else if (tool.id === 'contract') {
      this.router.navigate(['/contract/new']);
    } else if (tool.id === 'quote') {
      // Abrir bottom sheet de selección de tipo de construcción
      this.openWorkTypeSelector();
    } else if (tool.id === 'calculator') {
      this.analytics.trackEvent('calculator_flow', 'calculator');
      this.openCalculatorTypeSelector();
    } else {
        this.router.navigate([tool.route]);
    }


    // Si tiene ruta definida, navegar
    if (!!currentUser && tool.id === 'unicon') {
        //const currentUser = this.auth.user();
        this.router.navigate([tool.route], {
          queryParams: {
            firstName: currentUser?.firstname || currentUser?.name?.split(' ')[0] || 'Usuario',
            lastName: currentUser?.lastname || currentUser?.name?.split(' ').slice(1).join(' ') || 'Maestro',
            userIdOnBusiness: currentUser?.id || 'guest'
          }
        });
      }
  }

  // Manejador de eventos para ButtonSquareComponent
  onButtonSquareClick(tool: Tool): void {
    this.onToolClick(tool);
  }

  openConstructionModal(): void {
    this.showToolConstructionModal = true;
  }

  closeConstructionModal(): void {
    this.showToolConstructionModal = false;
  }

  private openRegisterModal(): void {
    this.bottomSheet.open(RegisterModal, {
      disableClose: false,
      hasBackdrop: true,
      panelClass: 'register-modal-bottom-sheet',
    });
  }

  private openWorkTypeSelector(): void {
    this.bottomSheet.open(WorkTypeSelectionModalComponent, {
      disableClose: false,
      hasBackdrop: true,
      panelClass: 'custom-bottom-sheet-quote',
    });
  }

  private openCalculatorTypeSelector(): void {
    const config: WorkTypeModalConfig = {
      title: '¿Qué vas a calcular?',
      subtitle: '',
      targetRoute: '/calculator',
      mode: 'calculator'
    };

    this.bottomSheet.open(WorkTypeSelectionModalComponent, {
      disableClose: false,
      hasBackdrop: true,
      panelClass: 'custom-bottom-sheet-quote',
      data: { config }
    });
  }

  private openTrainingUrl(): void {
    openTrainingUrl(this.auth.constructorRedirectUrl());
  }

  private scrollToLearnSection(): void {
    // Buscar primero por el ID específico de la sección, luego como fallback el componente
    const learnElement = document.querySelector('#aprende') || document.querySelector('app-learn-section');
    if (learnElement) {
      // Usar scroll suave hacia el elemento con un pequeño offset para mejor UX
      learnElement.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Fallback usando ViewportScroller si no encuentra el elemento
      this.viewportScroller.scrollToAnchor('aprende');
    }
  }

  getFormattedTitle(tool: Tool): string {
    switch(tool.id) {
      case 'quote':
        return 'Cotizar\nmateriales';
      case 'calculator':
        return 'Calcular\nMateriales';
      case 'contract':
        return 'Crear\nContrato';
      case 'work':
        return 'Buscar\nTrabajo';
      case 'unicon':
        return 'Pedir\nProductos\nUNICON';
      case 'training':
        return 'Capacitarme';
      default:
        return tool.title;
    }
  }

  /**
   * Determina si un tool está seleccionado
   */
  isToolSelected(tool: Tool): boolean {
    return this.selectedToolId === tool.id;
  }

  /**
   * Obtiene el estado del button-square basado en si está seleccionado
   */
  getToolState(tool: Tool): 'selected' | 'unselected' {
    return this.isToolSelected(tool) ? 'selected' : 'unselected';
  }

  // Método para obtener título plano sin HTML para ButtonSquareComponent
  getPlainTitle(tool: Tool): string {
    switch(tool.id) {
      case 'quote':
        return '¡Arma tu Lista de Materiales!';
      case 'calculator':
        return 'Calcular Materiales';
      case 'contract':
        return 'Crear Contrato';
      case 'work':
        return 'Buscar Trabajo';
      case 'unicon':
        return 'Pedir Productos UNICON';
      case 'training':
        return 'Capacitarme';
      default:
        return tool.title;
    }
  }
}
